import React from 'react'
import clsx from 'clsx'
import styles from './EntityMultiSelect.module.css'
import { Input } from '../../controls/Input/Input'
import { EntityMultiSelectRow } from './EntityMultiSelectRow'
import {
  BaseEntity,
  MAX_BULK_AUTOLOAD,
  UseInfiniteHook,
  useEntityMultiSelect,
} from './useEntityMultiSelect'

const SKELETON_ROWS = 5

interface Props<T extends BaseEntity> {
  value: number[]
  onChange: (ids: number[]) => void

  useInfiniteHook: UseInfiniteHook<T>
  searchParamName: string
  limit?: number

  getLabel: (item: T) => string

  disabled?: boolean
  className?: string
  itemClassName?: string
  query?: unknown

  markedIds?: number[]
  onMarkedChange?: (ids: number[]) => void
  renderMarkIcon?: (marked: boolean) => React.ReactNode
}

export function EntityMultiSelect<T extends BaseEntity>({
  value,
  onChange,
  useInfiniteHook,
  searchParamName,
  limit = 20,
  getLabel,
  disabled,
  className,
  itemClassName,
  query,
  markedIds = [],
  onMarkedChange,
  renderMarkIcon,
}: Props<T>) {
  const {
    search,
    setSearch,
    isTyping,
    onSearchKeyDown,

    visibleItems,
    selectedSet,
    markedSet,
    total,
    count,

    isLoading,
    isStale,
    isError,
    isFetchingNextPage,
    hasNextPage,
    loadedCount,
    refetch,

    toggle,
    toggleMark,
    selectAll,
    clearAll,
    isBulkLoading,
    showLoadedOnlyHint,
    showMissingHint,
    missingSelectedCount,

    listRef,
    sentinelRef,
    activeIndex,
    setActiveIndex,
    onListKeyDown,
  } = useEntityMultiSelect<T>({
    value,
    onChange,
    useInfiniteHook,
    searchParamName,
    limit,
    query,
    disabled,
    markedIds,
    onMarkedChange,
  })

  const showSkeleton = isLoading && !isStale
  const showProgress = isTyping || isLoading || isFetchingNextPage
  const canSelectAll = !disabled && (total === undefined || total > 0)
  const allLoadedSelected =
    visibleItems.length > 0 &&
    !hasNextPage &&
    visibleItems.every((item) => selectedSet.has(Number(item.id)))

  const selectAllLabel = isBulkLoading
    ? `Lade ${loadedCount}${total !== undefined ? `/${total}` : ''}…`
    : total !== undefined
      ? `Alle ${total} auswählen`
      : 'Alle auswählen'

  return (
    <div className={clsx(styles.main, className)}>
      <div className={styles.header}>
        {!disabled ? (
          <Input
            className={styles.searchInput}
            placeholder="Suchen…"
            value={search}
            onChange={setSearch}
            onKeyDown={onSearchKeyDown}
            clearable
            aria-label="Suchen"
          />
        ) : (
          <span />
        )}

        <span
          className={clsx(styles.countChip, count === 0 && styles.countChipEmpty)}
          aria-live="polite"
        >
          {count} ausgewählt
        </span>
      </div>

      <div className={styles.toolbar}>
        {canSelectAll && (
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={selectAll}
            disabled={isBulkLoading || allLoadedSelected}
          >
            {isBulkLoading && <span className={styles.spinner} aria-hidden />}
            {selectAllLabel}
          </button>
        )}

        {!disabled && count > 0 && (
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={clearAll}
            disabled={isBulkLoading}
          >
            Auswahl aufheben
          </button>
        )}

      </div>

      <div className={styles.progressTrack}>
        {showProgress && <span className={styles.progressBar} aria-hidden />}
      </div>

      <div
        ref={listRef}
        className={clsx(styles.list, isStale && styles.listStale)}
        role="listbox"
        aria-multiselectable
        aria-busy={isLoading || undefined}
        aria-label="Auswahlliste"
        onKeyDown={onListKeyDown}
      >
        {showSkeleton ? (
          Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <div key={index} className={styles.skeletonRow} aria-hidden />
          ))
        ) : isError ? (
          <div className={styles.status}>
            <p>Die Liste konnte nicht geladen werden.</p>
            {refetch && (
              <button
                type="button"
                className={styles.toolbarButton}
                onClick={() => refetch()}
              >
                Erneut versuchen
              </button>
            )}
          </div>
        ) : visibleItems.length === 0 ? (
          <p className={styles.status}>
            {search ? `Keine Ergebnisse für „${search}“` : 'Keine Einträge'}
          </p>
        ) : (
          <>
            {visibleItems.map((item, index) => {
              const id = Number(item.id)

              return (
                <EntityMultiSelectRow
                  key={id}
                  id={id}
                  index={index}
                  label={getLabel(item)}
                  checked={selectedSet.has(id)}
                  marked={markedSet.has(id)}
                  active={index === (activeIndex < 0 ? 0 : activeIndex)}
                  disabled={disabled}
                  showMark={Boolean(onMarkedChange)}
                  className={itemClassName}
                  onToggle={toggle}
                  onToggleMark={toggleMark}
                  onFocus={setActiveIndex}
                  renderMarkIcon={renderMarkIcon}
                />
              )
            })}

            <div ref={sentinelRef} className={styles.sentinel} aria-hidden />

            {isFetchingNextPage && (
              <p className={styles.loadingMore}>Weitere Einträge werden geladen…</p>
            )}
          </>
        )}
      </div>

      {showLoadedOnlyHint && (
        <p className={styles.hint}>
          {`Mehr als ${MAX_BULK_AUTOLOAD} Treffer – nur die geladenen ${loadedCount} Einträge wurden ausgewählt. Zum Erweitern weiterscrollen.`}
        </p>
      )}

      {showMissingHint && (
        <p className={styles.hint}>
          {missingSelectedCount === 1
            ? 'Ein ausgewählter Eintrag steht in dieser Liste nicht zur Verfügung.'
            : `${missingSelectedCount} ausgewählte Einträge stehen in dieser Liste nicht zur Verfügung.`}
        </p>
      )}
    </div>
  )
}
